import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrainCircuit, LockKeyhole, RefreshCw, ShieldCheck, Workflow } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { AIEngineeringControlPlaneCard } from '@/components/ai-engineering/AIEngineeringControlPlaneCard';
import { AIEngineeringGovernancePanel } from '@/components/ai-engineering/AIEngineeringGovernancePanel';
import { AIEngineeringTaskComposer } from '@/components/ai-engineering/AIEngineeringTaskComposer';
import { AIEngineeringTaskHeader } from '@/components/ai-engineering/AIEngineeringTaskHeader';
import { AIEngineeringTaskTimeline } from '@/components/ai-engineering/AIEngineeringTaskTimeline';
import { AIEngineeringAnalysisPanel } from '@/components/ai-engineering/AIEngineeringAnalysisPanel';
import { AIEngineeringDiffPanel } from '@/components/ai-engineering/AIEngineeringDiffPanel';
import { AIEngineeringLogsPanel } from '@/components/ai-engineering/AIEngineeringLogsPanel';
import { AIEngineeringHistoryTable } from '@/components/ai-engineering/AIEngineeringHistoryTable';
import { AIEngineeringApprovalBar } from '@/components/ai-engineering/AIEngineeringApprovalBar';
import { useAIEngineeringAccess } from '@/hooks/useAIEngineeringAccess';
import { useAIEngineeringFeatureFlag } from '@/hooks/useAIEngineeringFeatureFlag';
import { aiEngineeringAPI, getErrorMessage } from '@/lib/api';

const EMPTY_FORM = { title: '', request_text: '', module_target: 'reports', risk_level: 'medium' };
const EMPTY_SETTINGS_FORM = { provider: 'openai', model: '', timeout_seconds: '45', retry_attempts: '1', fallback_enabled: false, justification: 'Configuração inicial do control plane V2' };

export default function AIEngineeringPage() {
  const access = useAIEngineeringAccess();
  const featureFlag = useAIEngineeringFeatureFlag();
  const [form, setForm] = useState(EMPTY_FORM);
  const [controlPlane, setControlPlane] = useState(null);
  const [settingsForm, setSettingsForm] = useState(EMPTY_SETTINGS_FORM);
  const [credentialValue, setCredentialValue] = useState('');
  const [configBusy, setConfigBusy] = useState(false);
  const [credentialBusy, setCredentialBusy] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const handleFormChange = useCallback((field, value) => setForm((current) => ({ ...current, [field]: value })), []);
  const handleSettingsChange = useCallback((field, value) => setSettingsForm((current) => ({ ...current, [field]: value })), []);

  const syncSettingsForm = useCallback((payload) => {
    setSettingsForm({
      provider: payload?.provider || 'openai',
      model: payload?.model || '',
      timeout_seconds: String(payload?.timeout_seconds ?? 45),
      retry_attempts: String(payload?.retry_attempts ?? 1),
      fallback_enabled: Boolean(payload?.fallback_enabled),
      justification: 'Configuração actualizada no control plane V2',
    });
  }, []);

  const loadControlPlane = useCallback(async () => {
    if (!access.available) return;
    try {
      const response = await aiEngineeringAPI.getControlPlane();
      const payload = response.data || null;
      setControlPlane(payload);
      syncSettingsForm(payload);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar control plane AI Engineering'));
    }
  }, [access.available, syncSettingsForm]);

  const loadAuditLogs = useCallback(async () => {
    if (!access.available) return;
    try {
      setLoadingAudit(true);
      const response = await aiEngineeringAPI.listControlPlaneAudit();
      setAuditLogs(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar auditoria AI Engineering'));
    } finally {
      setLoadingAudit(false);
    }
  }, [access.available]);

  const loadTasks = useCallback(async (preferredTaskId = '') => {
    if (!access.available) return;
    try {
      setLoadingList(true);
      const response = await aiEngineeringAPI.listTasks();
      const nextTasks = response.data || [];
      setTasks(nextTasks);
      const nextSelectedId = preferredTaskId || selectedTaskId || nextTasks[0]?.id || '';
      setSelectedTaskId(nextSelectedId);
      if (!nextSelectedId) setSelectedTask(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar histórico AI Engineering'));
    } finally {
      setLoadingList(false);
    }
  }, [access.available, selectedTaskId]);

  const loadTaskDetail = useCallback(async (taskId) => {
    if (!taskId || !access.available) return;
    try {
      const response = await aiEngineeringAPI.getTask(taskId);
      setSelectedTask(response.data || null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar detalhe da task'));
    }
  }, [access.available]);

  useEffect(() => {
    if (access.available) {
      loadTasks().catch(() => null);
      loadControlPlane().catch(() => null);
      loadAuditLogs().catch(() => null);
    } else {
      setControlPlane(null);
      setAuditLogs([]);
      setTasks([]);
      setSelectedTaskId('');
      setSelectedTask(null);
    }
  }, [access.available, loadAuditLogs, loadControlPlane, loadTasks]);

  useEffect(() => {
    if (selectedTaskId) {
      loadTaskDetail(selectedTaskId).catch(() => null);
    }
  }, [loadTaskDetail, selectedTaskId]);

  const createTask = useCallback(async () => {
    try {
      setCreating(true);
      const response = await aiEngineeringAPI.createTask(form);
      setForm(EMPTY_FORM);
      setSelectedTask(response.data);
      setSelectedTaskId(response.data.id);
      toast.success('Task AI Engineering criada');
      await loadTasks(response.data.id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar task AI Engineering'));
    } finally {
      setCreating(false);
    }
  }, [form, loadTasks]);

  const saveControlPlaneSettings = useCallback(async () => {
    try {
      setConfigBusy(true);
      const response = await aiEngineeringAPI.updateControlPlane({
        provider: settingsForm.provider || null,
        model: settingsForm.model || null,
        timeout_seconds: Number(settingsForm.timeout_seconds),
        retry_attempts: Number(settingsForm.retry_attempts),
        fallback_enabled: Boolean(settingsForm.fallback_enabled),
        justification: settingsForm.justification,
      });
      setControlPlane(response.data || null);
      syncSettingsForm(response.data || null);
      toast.success('Control plane V2 actualizado');
      await loadAuditLogs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar settings do control plane'));
    } finally {
      setConfigBusy(false);
    }
  }, [loadAuditLogs, settingsForm, syncSettingsForm]);

  const saveCredentialOverride = useCallback(async () => {
    try {
      setCredentialBusy(true);
      const response = await aiEngineeringAPI.updateCredentialOverride({
        api_key: credentialValue,
        justification: 'Rotação controlada do override administrativo seguro',
      });
      setCredentialValue('');
      setControlPlane(response.data || null);
      syncSettingsForm(response.data || null);
      toast.success('Override seguro guardado');
      await loadAuditLogs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar override seguro'));
    } finally {
      setCredentialBusy(false);
    }
  }, [credentialValue, loadAuditLogs, syncSettingsForm]);

  const clearCredentialOverride = useCallback(async () => {
    try {
      setCredentialBusy(true);
      const response = await aiEngineeringAPI.clearCredentialOverride();
      setCredentialValue('');
      setControlPlane(response.data || null);
      syncSettingsForm(response.data || null);
      toast.success('Override seguro removido');
      await loadAuditLogs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao remover override seguro'));
    } finally {
      setCredentialBusy(false);
    }
  }, [loadAuditLogs, syncSettingsForm]);

  const runAction = useCallback(async (requestFactory, successMessage) => {
    if (!selectedTaskId) return;
    try {
      setActionBusy(true);
      const response = await requestFactory(selectedTaskId);
      setSelectedTask(response.data);
      toast.success(successMessage);
      await loadTasks(selectedTaskId);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro no fluxo AI Engineering'));
    } finally {
      setActionBusy(false);
    }
  }, [loadTasks, selectedTaskId]);

  const visibilitySummary = useMemo(() => `${tasks.length} task(s) visíveis nesta sessão`, [tasks.length]);
  const permissionMatrix = controlPlane?.permissions || {};
  const canCreateTask = Boolean(permissionMatrix['ai_engineering.create']?.allowed);

  const toggleFeatureFlag = useCallback(async () => {
    const nextEnabled = !featureFlag.enabled;
    const justification = nextEnabled
      ? 'Activação administrativa controlada do módulo AI Engineering.'
      : 'Desactivação administrativa controlada do módulo AI Engineering.';
    try {
      await featureFlag.setEnabled(nextEnabled, justification);
      await access.refresh();
      toast.success(nextEnabled ? 'AI Engineering activado' : 'AI Engineering desactivado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível actualizar o estado do AI Engineering'));
    }
  }, [access, featureFlag]);

  if (access.loading) {
    return <div className="space-y-4 bg-slate-50 p-4" data-testid="ai-engineering-page"><Card className="border-slate-200"><CardContent className="flex items-center gap-3 p-6 text-sm text-slate-600"><RefreshCw className="h-4 w-4 animate-spin" /> A validar disponibilidade do cockpit AI Engineering...</CardContent></Card></div>;
  }

  if (!access.available) {
    return (
      <div className="space-y-4 bg-slate-50 p-4" data-testid="ai-engineering-page">
        <Card className="border-slate-300 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl text-slate-950"><LockKeyhole className="h-5 w-5 text-slate-700" /> AI Engineering bloqueado</CardTitle>
            <CardDescription>{access.reason === 'feature_flag_off' ? 'A feature flag está OFF. O menu deve permanecer oculto e a página não opera em modo normal.' : access.detail || 'Acesso restrito ao módulo AI Engineering.'}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={access.refresh} data-testid="ai-engineering-refresh-availability-button">Revalidar disponibilidade</Button>
            {featureFlag.isAdminOnly && (
              <Button
                variant={featureFlag.enabled ? 'destructive' : 'default'}
                onClick={toggleFeatureFlag}
                disabled={featureFlag.loading || featureFlag.busy}
                data-testid="ai-engineering-feature-flag-toggle-button"
              >
                {featureFlag.busy ? 'A actualizar...' : featureFlag.enabled ? 'Desactivar módulo' : 'Activar módulo'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 bg-slate-50 p-4" data-testid="ai-engineering-page" style={{ fontFamily: "'IBM Plex Sans', Inter, sans-serif" }}>
      <div className="space-y-3 rounded-sm border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950">AI Engineering</h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">Governance cockpit para pedidos técnicos internos, com Analyze Real, Suggest Fix Real auditável, aprovação humana e bloqueio explícito de qualquer acção real sobre o repositório.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline" className="rounded-sm border-slate-300 bg-slate-100 text-slate-700"><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Admin only</Badge>
            <Badge variant="outline" className="rounded-sm border-blue-200 bg-blue-50 text-blue-700"><Workflow className="mr-1 h-3.5 w-3.5" /> Analyze + Suggest Fix real</Badge>
            <Badge variant="outline" className="rounded-sm border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="ai-engineering-v2-control-plane-badge">V2 control plane activo</Badge>
            <Badge variant="outline" className="rounded-sm border-slate-300 bg-white text-slate-700"><BrainCircuit className="mr-1 h-3.5 w-3.5" /> {visibilitySummary}</Badge>
            {featureFlag.isAdminOnly && (
              <Button
                variant={featureFlag.enabled ? 'destructive' : 'outline'}
                size="sm"
                className="h-8 rounded-sm px-3 text-xs"
                onClick={toggleFeatureFlag}
                disabled={featureFlag.loading || featureFlag.busy}
                data-testid="ai-engineering-header-toggle-button"
              >
                {featureFlag.busy ? 'A actualizar...' : featureFlag.enabled ? 'Desactivar módulo' : 'Activar módulo'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
        <AIEngineeringControlPlaneCard
          config={controlPlane}
          settingsForm={settingsForm}
          onSettingsChange={handleSettingsChange}
          onSaveSettings={saveControlPlaneSettings}
          savingSettings={configBusy}
          credentialValue={credentialValue}
          onCredentialChange={setCredentialValue}
          onSaveCredential={saveCredentialOverride}
          onClearCredential={clearCredentialOverride}
          savingCredential={credentialBusy}
        />
        <AIEngineeringGovernancePanel config={controlPlane} auditLogs={auditLogs} loadingAudit={loadingAudit} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[360px,minmax(0,1fr)]">
        <div className="space-y-4">
          <AIEngineeringTaskComposer form={form} onChange={handleFormChange} onSubmit={createTask} submitting={creating} disabled={!canCreateTask} helperText={canCreateTask ? '' : 'A criação de tasks depende da permissão local ai_engineering.create.'} />
          <AIEngineeringHistoryTable tasks={tasks} selectedTaskId={selectedTaskId} onSelectTask={setSelectedTaskId} onRefresh={() => loadTasks(selectedTaskId)} loading={loadingList} />
        </div>

        <div className="space-y-4">
          {selectedTask ? (
            <>
              <AIEngineeringTaskHeader task={selectedTask} />
              <div className="grid gap-4 xl:grid-cols-[0.9fr,1.1fr]">
                <AIEngineeringTaskTimeline task={selectedTask} />
                <AIEngineeringAnalysisPanel task={selectedTask} />
              </div>
              {selectedTask?.patch_diff ? <AIEngineeringDiffPanel task={selectedTask} /> : null}
              <AIEngineeringLogsPanel task={selectedTask} />
            </>
          ) : (
            <EmptyStatePanel title="Cockpit pronto para operar" description="Crie a primeira task ou seleccione uma entrada do histórico para abrir timeline, diff, logs e barra de aprovação." ctaLabel={null} testId="ai-engineering-page-empty-state" />
          )}
        </div>
      </div>

      <AIEngineeringApprovalBar
        task={selectedTask}
        actionBusy={actionBusy}
        permissionMatrix={permissionMatrix}
        onAnalyze={() => runAction(aiEngineeringAPI.analyzeTask, 'Analyze Real concluído')}
        onGeneratePatch={() => runAction(aiEngineeringAPI.generatePatch, 'Suggest Fix Real concluído')}
        onRunTests={() => runAction(aiEngineeringAPI.runTests, 'Testes mockados executados')}
        onApprove={() => runAction(aiEngineeringAPI.approveTask, 'Task aprovada internamente')}
        onReject={(reason) => runAction((taskId) => aiEngineeringAPI.rejectTask(taskId, { reason }), 'Task rejeitada com motivo registado')}
        onOpenPr={() => runAction(aiEngineeringAPI.openPr, 'Payload de PR mockado preparado')}
      />
    </div>
  );
}